/**
 * מאזן — public front door at /Mazan (signed-out visitors). Copy adapted from the
 * partner's prototype landing page; the price comes from the admin setting.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, FileText, Lock, Upload } from 'lucide-react'
import api from '../../services/api'
import { MAZAN } from '../../config/tracks'
import { ils } from '../../features/mazan/model'

const CONTENTS = [
  'חוות דעת כלכלית מלאה, בחתימת מומחה רשום בבתי המשפט',
  'פסיקתא לכל קופת פנסיה לפי חוק לחלוקת חיסכון פנסיוני, התשע״ד–2014',
  'היוון הקצבאות עד המועד המוקדם מבין תוחלות החיים של בני הזוג',
  'איזון נכסים הוניים — קרן השתלמות, גמל להשקעה, ביטוח מנהלים — כולל מס רווחי הון',
  'סיכום נטו לתשלום בין הצדדים, בקיזוז הדדי',
]

const STEPS = [
  { Icon: Upload, title: 'מעלים את דוח המסלקה', text: 'PDF, XML או ZIP — הקובץ נקרא במכשיר שלכם, והנתונים ממולאים אוטומטית.' },
  { Icon: Check, title: 'משלימים את פרטי התיק', text: 'מועד הקרע, מועד הנישואין ופרטי ההליך — ובודקים שהכול מוכן לחישוב.' },
  { Icon: FileText, title: 'משלמים ומקבלים את התוצאות', text: 'הדוח המלא מופק מיד כ־PDF, נשלח למייל ונשמר באזור האישי.' },
]

export default function MazanLandingPage() {
  const [price, setPrice] = useState<number | null>(null)

  useEffect(() => {
    api.get<{ priceIls: number }>('/mazan/info').then((r) => setPrice(r.data.priceIls)).catch(() => setPrice(null))
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-bl from-emerald-50 via-white to-white" dir="rtl">
      <div className="mx-auto grid w-full max-w-6xl gap-10 px-4 py-12 lg:grid-cols-2 lg:py-20">
        <section>
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-950">
            <MAZAN.Icon size={16} aria-hidden="true" />
            {MAZAN.shortName}
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold text-gray-900 sm:text-4xl">איזון משאבים וזכויות פנסיוניות</h1>
          <p className="mt-3 text-lg text-gray-800">{MAZAN.tagline} — דוח מקצועי, מוכן להגשה, בתוך דקות.</p>

          <h2 className="mt-8 text-lg font-semibold text-gray-900">מה כולל הדוח</h2>
          <ul className="mt-3 space-y-2">
            {CONTENTS.map((c) => (
              <li key={c} className="flex items-start gap-2 text-gray-800">
                <Check size={18} className="mt-0.5 flex-shrink-0 text-emerald-800" aria-hidden="true" />
                {c}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={MAZAN.signupPath} className="btn btn-primary">התחלת תחשיב</Link>
            <Link to={MAZAN.loginPath} className="btn btn-secondary">כבר יש לי חשבון</Link>
          </div>
          {price != null && price > 0 && (
            <p className="mt-4 text-gray-800">
              מחיר הדוח: <strong>{ils.format(price)}</strong> · תשלום חד־פעמי — התוצאות והדוח המלא מוצגים מיד לאחר התשלום
            </p>
          )}
        </section>

        <section aria-labelledby="mz-how" className="card self-start">
          <h2 id="mz-how" className="text-lg font-semibold text-gray-900">איך זה עובד</h2>
          <ol className="mt-4 space-y-5">
            {STEPS.map(({ Icon, title, text }, k) => (
              <li key={title} className="flex items-start gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-800 text-white" aria-hidden="true">
                  <Icon size={18} />
                </span>
                <div>
                  <p className="font-semibold text-gray-900"><span className="sr-only">שלב {k + 1}: </span>{title}</p>
                  <p className="text-sm text-gray-700">{text}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-6 flex items-start gap-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
            <Lock size={16} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            הנתונים נשמרים מוצפנים ומשמשים להפקת הדוח בלבד. התשלום מאובטח דרך CardCom (אשראי, Bit, Apple Pay, Google Pay).
          </p>
          <p className="mt-3 text-xs text-gray-600">
            מקרים שדורשים תחשיב פרטני — פנסיית קיבוץ, קרן פנסיה ותיקה — מטופלים ישירות מול יפתח ונגר, 050-397-6397.
          </p>
        </section>
      </div>
    </div>
  )
}
